import React, { useCallback, useEffect, useRef, useState } from "react";
import { CalendarClock, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ExternalLink, FileText, HandCoins, Mail, Pencil, Plus, Settings2, Undo2, UserX, Wallet, X } from "lucide-react";
import { api, ouvrirPdf } from "./api";
import { Badge, BoutonSombre, EnTeteMenu, Modale, Puces, Section } from "./ui";
import { ChiffresPrives } from "./DashboardPlus";
import CalendrierPrives from "../components/CalendrierPrives";
import { ContenuModification, useModification } from "../components/ModifierCoursPrive";
import { AjoutFait, ContenuAjout, useAjout } from "../components/AjouterCoursPrive";
import { choixAbsence, detailSalaire, duree as dureeTexte, franc, grilleTexte, heures, moinsDe, partCourte, pourRienTexte, regleSalaire, regles as reglesDe, due as dueAccord } from "../lib/reglesPrives";

/* Menu Cours privés, la réservation de la console (école, 30.10.2026 ; remplace
   Calendly, version de test d'abord). Ce qu'on y vient faire, dans l'ordre (Jen, 02.11.2026 :
   « la liste des cours réservés en premier avant le calendrier — tu en mets pas trop par
   défaut — ce mois, avec un système de repliage ») :
   1. « Cours réservés » : ce mois-ci d'abord, en liste compacte (date, heure, élève, prof,
      contact, prix), repliée au-delà de 5 lignes ; « À venir », « Passés », « Annulés » à côté.
      « Modifier… » (Jen, 02.11.2026 : « l'école doit pouvoir tout faire ») : la date et l'heure,
      la durée, le prof, la discipline, la note — la famille et le prof sont prévenus.
      Annuler (la famille reçoit un e-mail avec un mot), « Absent·e — facturer », la facture.
      Toucher une date : ce jour s'ouvre dans le calendrier.
   2. Le « Calendrier » (Jen, 31.10.2026 : le calendrier et les disponibilités des profs ne
      font qu'un) : le mois — en vert les jours qui ont un cours réservé —, la journée de chaque
      prof, et « Modifier les disponibilités » de n'importe quel prof, ce jour seulement ou
      toutes les semaines.
   3. « À payer aux profs (cash) » (Jen, 02.11.2026 : « payé cash par l'école … on les sort des
      décomptes mensuels ») : par mois, le salaire de chaque prof — 30.– pour 30 min, 40.– pour
      45 min, 50.– pour 60 min, + 5.– de déplacement ; déplacé pour rien, 50 % du salaire + le
      déplacement (Jen, 02.11.2026 au soir). Noémie et Miller, propriétaires : rien.
   Les règles écrites ici (24 h, 2 mois, 15 minutes, la moitié, moins d'1 h…) sont celles des
   Réglages › Cours privés (`d.regles`, Jen 02.11.2026) ; « Réglages des cours privés » y mène. */

const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
const LIMITE = 5;               // les lignes montrées avant « Voir les N autres »
const NOMS_MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const JOURS_COURTS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const heure = (h) => String(h || "").replace(/^0/, "").replace(":00", "h").replace(":", "h");      // « 09:30 » → « 9h30 »
// Les listes de « Réservations », et celle qu'un chiffre clé (ici ou au tableau de bord :
// cible « annulees: ») demande d'ouvrir.
const VUES = ["a_venir", "mois", "passees", "annulees"];
const vueDe = (cible) => {
  const v = /^([a-z_]+):/.exec(String(cible || ""))?.[1];
  return VUES.includes(v) ? v : "";
};
const aujourdhui = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Zurich" });
// Les annulés de la SAISON (depuis le 1er août), comme le chiffre clé.
const debutSaison = () => { const [a, m] = aujourdhui().split("-").map(Number); return `${m >= 8 ? a : a - 1}-08-01`; };
// La facture d'une annulation tardive ou d'une absence : « Facture F-… · 50 % dus », ou son état.
const etatFacture = (f) => (f.statut === "Annulée" ? "annulée" : f.statut === "Payée" ? "payée"
  : f.statut === "Partiellement payée" ? "payée en partie" : `${f.pourcentage || 50} % dus`);
// L'heure du cours est passée (le prof était là) : « Absent·e — facturer » ; à moins de 24 h
// (ou passée), l'école qui annule pour la famille peut facturer 50 % (Jen, 01.11.2026). Les
// deux seuils viennent des Réglages (`R`).
const debutDe = (r) => new Date(`${r.date}T${r.debut}:00`).getTime();
const commence = (r) => debutDe(r) <= Date.now();
const tardPourEcole = (r, R) => debutDe(r) - Date.now() < R.ecole_facturer_heures * 60 * 60 * 1000;
// Moins d'1 h avant le cours, le prof est déjà en route : il est payé (Jen, 30.10.2026).
const deplace = (r, R) => debutDe(r) - Date.now() < R.deplacement_minutes * 60 * 1000;
// « moins d'1 h avant » : le seuil « prof déplacé » des Réglages.
const seuilDeplace = (R) => moinsDe(dureeTexte(R.deplacement_minutes));
/* Aller à Réglages › Cours privés (Jen, 02.11.2026 : « tout ça dans les réglages »). */
const versReglages = () => {
  try { sessionStorage.setItem("reglages_groupe", "prives"); } catch { /* rien */ }
  window.dispatchEvent(new CustomEvent("console-aller", { detail: { onglet: "reglages" } }));
};
// « Facture F-… · 50 % dus » (payée, annulée) : un clic ouvre le PDF.
const lienFacture = (f) => (
  <button type="button" onClick={() => ouvrirPdf(`/reservations-privees/factures/${f.no_facture}/pdf`, `Facture ${f.no_facture}`)}
    className="inline-flex items-start gap-1 text-left text-[11px] font-semibold underline underline-offset-2"
    style={{ color: f.statut === "Annulée" ? "var(--muted)" : f.statut === "Payée" ? "#15803D" : "#B45309" }}
    title="Voir la facture (PDF) — Facturation › Cours privés" data-testid="prives-facture">
    <FileText size={12} className="shrink-0 mt-px" /> <span>Facture <span className="whitespace-nowrap">{f.no_facture}</span> · {etatFacture(f)}</span>
  </button>
);

/* « Absent·e — facturer » : la famille n'est pas venue et n'avait pas annulé. 50 % proposés
   (la règle publiée : une absence est une annulation au dernier moment), ou le prix entier ;
   « Ne rien facturer » ferme. Le serveur marque la réservation, crée la facture et l'envoie. */
function ModaleAbsence({ r, R, regle, onFermer, onFait }) {
  const [pct, setPct] = useState(Number(R.absence_pourcentage));
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const pourQui = r.pour === "enfant" && r.eleve_prenom ? `${r.eleve_prenom} ${r.eleve_nom || ""}`.trim() : `${r.prenom} ${r.nom}`;
  const facturer = async () => {
    setEnvoi(true); setErreur("");
    try {
      onFait(await api(`/reservations-privees/${r.id}/absent`, { method: "POST", body: JSON.stringify({ pourcentage: pct }) }));
    } catch (e) { setErreur(e.message); setEnvoi(false); }
  };
  return (
    <Modale titre="Absent·e sans avoir annulé ?" sous_titre={`${r.discipline_label} · ${String(r.quand || "").replace(/ à .*$/, "")} · ${r.debut}–${r.fin} · avec ${r.prof}`}
      onFermer={envoi ? () => {} : onFermer} testid="prives-absence">
      <p className="text-sm">
        <strong>{pourQui}</strong> n'est pas venu·e et le cours n'avait pas été annulé. La facture part par e-mail à {r.email} (PDF joint, comment payer). {r.prof_salarie ? `${r.prof} touche quand même ${pourRienTexte(regle)} (déplacé·e pour rien).` : ""}
      </p>
      <div className="mt-4 space-y-2" role="radiogroup" aria-label="Montant à facturer">
        {choixAbsence(R, " ; une absence est une annulation au dernier moment").map(([p, titre, aide]) => (
          <label key={p} className="flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer"
            style={pct === p ? { borderColor: "var(--primary)", background: "var(--primary-tint)" } : { borderColor: "var(--border)" }}
            data-testid={`prives-absence-${p}`}>
            <input type="radio" name="absence-pourcentage" className="mt-1 accent-[var(--primary)]" checked={pct === p} onChange={() => setPct(p)} />
            <span className="flex-1 text-sm">
              <span className="flex justify-between gap-3 font-semibold"><span>{titre}</span><span className="whitespace-nowrap">{chf((Number(r.montant || 0) * p) / 100)}</span></span>
              {aide && <span className="block text-xs text-[var(--muted)] mt-0.5">{aide}</span>}
            </span>
          </label>
        ))}
      </div>
      {erreur && <p className="text-sm mt-3" style={{ color: "#B91C1C" }} data-testid="prives-absence-erreur">{erreur}</p>}
      <div className="flex flex-wrap gap-2 mt-5">
        <button className="btn-ghost" onClick={onFermer} disabled={envoi} data-testid="prives-absence-rien">Ne rien facturer</button>
        <BoutonSombre onClick={facturer} disabled={envoi} testid="prives-absence-facturer">
          {envoi ? "Un instant…" : `Facturer ${chf((Number(r.montant || 0) * pct) / 100)}`}
        </BoutonSombre>
      </div>
    </Modale>
  );
}

/* « Modifier le cours » (02.11.2026) : le formulaire partagé avec l'app école
   (components/ModifierCoursPrive), dans une fenêtre ; « Enregistrer » reste grisé tant que rien
   ne change ou que le moment choisi est impossible. */
function ModaleModification({ r, onFermer, onFait }) {
  const m = useModification(r, onFait);
  return (
    <Modale titre="Modifier le cours" sous_titre={`${r.discipline_label} · ${`${r.eleve_prenom || ""} ${r.eleve_nom || ""}`.trim()} · ${r.quand} · avec ${r.prof}`}
      onFermer={m.envoi ? () => {} : onFermer} testid="prives-modif" large>
      <ContenuModification m={m} />
      <div className="flex flex-wrap items-center justify-end gap-2 mt-5">
        {!m.change && m.d?.modifiable && <span className="text-xs text-[var(--muted)] mr-auto">Change la date, l'heure, la durée, le prof… puis enregistre.</span>}
        <button className="btn-ghost" onClick={onFermer} disabled={m.envoi} data-testid="prives-modif-garder">Garder</button>
        <BoutonSombre onClick={m.enregistrer} disabled={!m.pret} testid="prives-modif-enregistrer">
          {m.envoi ? "Un instant…" : "Enregistrer la modification"}
        </BoutonSombre>
      </div>
    </Modale>
  );
}

/* « Ajouter un cours » (retour de l'école, 03.11.2026) : un cours réservé par téléphone ou au
   comptoir, ou un cours déjà donné (avant l'application) — components/AjouterCoursPrive. La
   fenêtre reste ouverte après l'ajout : « Encore un cours pour … » enchaîne une série. */
function ModaleAjout({ depart, onFermer, onFait }) {
  const m = useAjout(onFait, depart);
  return (
    <Modale titre="Ajouter un cours privé" sous_titre="Réservé par téléphone, au comptoir — ou un cours déjà donné"
      onFermer={m.envoi ? () => {} : onFermer} testid="prives-ajout" large>
      {m.fait ? <AjoutFait m={m} onFermer={onFermer} /> : (
        <>
          <ContenuAjout m={m} />
          <div className="flex flex-wrap items-center justify-end gap-2 mt-5">
            <button className="btn-ghost" onClick={onFermer} disabled={m.envoi} data-testid="prives-ajout-annuler">Annuler</button>
            <BoutonSombre onClick={m.ajouter} disabled={!m.pret} testid="prives-ajout-ok">
              {m.envoi ? "Un instant…" : m.passe ? "Noter ce cours déjà donné" : "Ajouter le cours"}
            </BoutonSombre>
          </div>
        </>
      )}
    </Modale>
  );
}

/* Le détail d'un prof : chaque cours privé du mois et son salaire, avec le calcul en clair
   (« 30 min · salaire 30.– + déplacement 5.– »). Un cours où il s'est déplacé pour rien le dit
   (« Famille absente · 50 % du salaire de 30 min + déplacement 5.– ») — Jen, 02.11.2026 : « c'est
   seulement pour les annulations ? ». */
function DetailSalaire({ lignes, seuil }) {
  if (!lignes.length) return <div className="px-5 pb-3 text-xs text-[var(--muted)]">Aucun cours compté pour l'instant.</div>;
  return (
    <div className="px-5 pb-3" data-testid="prives-salaires-detail">
      {lignes.map((l) => {
        const d = new Date(`${l.date}T12:00:00`);
        const quand = `${JOURS_COURTS[d.getDay()]} ${l.date.slice(8, 10)}.${l.date.slice(5, 7)} · ${l.debut}–${l.fin}`;
        return (
          <div key={l.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5 text-xs" style={{ borderTop: "1px dashed #E2E8F0" }} data-testid="prives-salaires-ligne">
            <span className="tabular-nums text-[var(--muted)] w-[118px] shrink-0">{quand}</span>
            <span className="font-medium">{l.eleve || "Élève"}</span>
            {l.discipline_label && <span className="text-[var(--muted)]">{l.discipline_label}</span>}
            {l.a_venir && <span className="text-[var(--muted)]">· à venir</span>}
            {l.pour_rien ? (
              <span className="ml-auto px-2 py-0.5 rounded-full font-medium" style={{ background: "#FFF7ED", color: "#B45309" }} data-testid="prives-salaires-calcul">
                {detailSalaire(l, seuil)}
              </span>
            ) : (
              <span className="ml-auto tabular-nums text-[var(--muted)]" data-testid="prives-salaires-calcul">{detailSalaire(l, seuil)}</span>
            )}
            <span className="tabular-nums font-semibold w-[84px] text-right">{chf(l.salaire)}</span>
          </div>
        );
      })}
    </div>
  );
}

/* « À payer aux profs (cash) » : le salaire de chaque prof, mois par mois — payé en cash par
   l'école, hors des décomptes mensuels. Le mois en cours d'abord, les flèches pour les autres ;
   « Modifier » mène à Réglages › Cours privés › Salaire du prof (la grille, le déplacement, la
   règle des 50 %). */
function SalairesDesProfs({ salaires, seuil }) {
  const mois = salaires?.mois || [];
  const [i, setI] = useState(() => Math.max(0, mois.findIndex((m) => m.mois === aujourdhui().slice(0, 7))));
  const [ouvert, setOuvert] = useState("");
  if (!mois.length) return null;
  const m = mois[Math.min(i, mois.length - 1)];
  const g = regleSalaire(salaires.regle);
  return (
    <Section titre="À payer aux profs (cash)" testid="prives-salaires"
      aide={`Le salaire des cours privés, que l'école remet en cash à chaque prof (pas à Noémie ni à Miller, propriétaires) — hors des décomptes mensuels. Cours donné : ${grilleTexte(g)}, + ${franc(g.deplacement)} de déplacement par cours. Prof déplacé pour rien (famille absente, annulation ${seuil} avant créditée, annulation de l'école « prof déplacé ») : ${pourRienTexte(g)}. Touche un prof pour voir ses cours un par un.`}
      action={(
        <div className="inline-flex items-center gap-1">
          <button type="button" className="w-8 h-8 rounded-full grid place-items-center hover:bg-[#F1F5F9] disabled:opacity-30" disabled={i <= 0}
            onClick={() => setI(i - 1)} aria-label="Mois précédent" data-testid="prives-salaires-avant"><ChevronLeft size={16} /></button>
          <span className="text-sm font-medium min-w-[120px] text-center" data-testid="prives-salaires-mois">{m.mois_label}</span>
          <button type="button" className="w-8 h-8 rounded-full grid place-items-center hover:bg-[#F1F5F9] disabled:opacity-30" disabled={i >= mois.length - 1}
            onClick={() => setI(i + 1)} aria-label="Mois suivant" data-testid="prives-salaires-apres"><ChevronRight size={16} /></button>
        </div>
      )}>
      <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="prives-salaires-liste">
        {!m.profs.length ? (
          <div className="px-5 py-5 text-sm text-[var(--muted)]">Aucun cours privé en {m.mois_label.split(" ")[0]}.</div>
        ) : (
          <>
            {m.profs.map((p, k) => (
              <div key={p.prof_id} style={k ? { borderTop: "1px solid #F1F5F9" } : undefined}>
                <button type="button" className="w-full flex items-center gap-3 px-5 py-3 text-sm text-left hover:bg-[#F8FAFC]"
                  onClick={() => setOuvert(ouvert === p.prof_id ? "" : p.prof_id)} aria-expanded={ouvert === p.prof_id} data-testid="prives-salaires-prof">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.couleur || "#94A3B8" }} />
                  <span className="font-semibold">{p.prenom}</span>
                  <span className="text-[var(--muted)]">
                    {p.nb} cours{p.nb_a_venir ? ` (dont ${p.nb_a_venir} à venir)` : ""}{p.a_confirmer ? ` · ${p.a_confirmer} à confirmer` : ""}
                  </span>
                  <span className="ml-auto font-semibold tabular-nums">{chf(p.total)}</span>
                  {ouvert === p.prof_id ? <ChevronUp size={15} className="text-[var(--muted)]" /> : <ChevronDown size={15} className="text-[var(--muted)]" />}
                </button>
                {ouvert === p.prof_id && <DetailSalaire lignes={p.lignes || []} seuil={seuil} />}
              </div>
            ))}
            <div className="flex items-center gap-3 px-5 py-3 text-sm" style={{ borderTop: "1px solid var(--border)", background: "#F8FAFC" }} data-testid="prives-salaires-total">
              <span className="font-semibold">Total</span>
              <span className="ml-auto font-semibold tabular-nums">{chf(m.total)}</span>
            </div>
          </>
        )}
      </div>
      <div className="mt-2 text-xs text-[var(--muted)] flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="prives-salaires-regle">
        <span>Salaire des profs : {grilleTexte(g)}, + {franc(g.deplacement)} de déplacement ; déplacé pour rien : {pourRienTexte(g)}.</span>
        <button type="button" className="underline underline-offset-2" onClick={versReglages} data-testid="prives-salaires-modifier">Modifier</button>
      </div>
    </Section>
  );
}

/* « Modifié le 02.11 · avant : lundi 9 novembre à 17h, 30 min, avec Lydia » : ce qui a changé. */
const jj = (instant) => { const d = new Date(instant || ""); return Number.isNaN(d.getTime()) ? "" : ` le ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`; };
const Changement = ({ r }) => {
  const avant = [r.ancien_quand, r.ancien_minutes > 0 ? `${r.ancien_minutes} min` : "", r.repris_de ? `avec ${r.repris_de}` : ""].filter(Boolean);
  if (!avant.length || r.statut === "annulee") return null;
  return (
    <div className="text-xs mt-0.5" style={{ color: "#B45309" }} data-testid="prives-modifie">
      {r.modifiee_le ? `Modifié par l'école${jj(r.modifiee_le)}` : `Déplacé par la famille${jj(r.deplacee_le)}`} · avant : {avant.join(", ")}
    </div>
  );
};

/* La date d'un groupe de cours, comme une page d'agenda : « jeu. / 5 / nov. ». Toucher : ce
   jour s'ouvre dans le calendrier, plus bas. */
const FeuilleDate = ({ date, onVoir }) => {
  const d = new Date(`${date}T12:00:00`);
  const auj = date === aujourdhui();
  return (
    <button type="button" onClick={() => onVoir(date)} title="Voir ce jour dans le calendrier" data-testid="prives-ligne-date"
      className="w-12 rounded-xl py-1 text-center hover:bg-[var(--primary-tint)] transition-colors">
      <span className="block text-[10px] font-semibold uppercase tracking-wide" style={{ color: auj ? "#0E7490" : "var(--muted)" }}>{auj ? "auj." : JOURS_COURTS[d.getDay()]}</span>
      <span className="block text-xl font-semibold leading-none tabular-nums mt-0.5" style={{ color: auj ? "#0E7490" : undefined }}>{d.getDate()}</span>
      <span className="block text-[10px] text-[var(--muted)] mt-0.5">{MOIS_COURTS[d.getMonth()]}</span>
    </button>
  );
};

/* Ce que l'école peut faire d'un cours réservé, au bout de sa ligne : « Absent·e — facturer »
   (l'heure est passée), « Annuler… » (à venir), ou l'état d'un cours annulé / absent avec sa
   facture. Le vert « Réservé » sur chaque ligne n'apportait rien : seul l'inhabituel se dit. */
const EtatEtGestes = ({ r, aVenir, enCours, onAbsent, onAnnuler, onModifier }) => {
  if (r.statut === "annulee") {
    return (
      <span className="inline-flex flex-col items-end gap-1">
        {/* La règle qui s'est appliquée à CETTE annulation (gardée sur la réservation). */}
        <Badge ton={r.tardive ? "ambre" : "neutre"}>{r.tardive ? `Annulé < ${heures(r.tardive_heures || 24)} · ${r.tardive_pourcentage || 50} % dû` : `Annulé (${r.annulee_par === "famille" ? "famille" : "école"})`}</Badge>
        {r.prof_paye && <Badge ton="bleu">prof payé (déplacé)</Badge>}
        {r.paie_a_confirmer && <Badge ton="ambre">prof : à confirmer</Badge>}
        {/* La facture automatique d'une annulation tardive par la famille (01.11.2026). */}
        {r.facture && lienFacture(r.facture)}
      </span>
    );
  }
  if (r.absent) {
    // Absent·e sans annuler (01.11.2026) : le cours reste payé au prof, la famille a sa facture.
    return (
      <span className="inline-flex flex-col items-end gap-1" data-testid="prives-absent">
        <Badge ton="ambre">Absent·e</Badge>
        {r.facture && lienFacture(r.facture)}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap justify-end items-center gap-1.5">
      {commence(r) && !enCours && (
        <button className="btn-ghost text-xs whitespace-nowrap" title="La famille n'est pas venue et n'avait pas annulé : facturer"
          onClick={() => onAbsent(r)} data-testid="prives-absent-facturer"><UserX size={12} /> Absent·e — facturer</button>
      )}
      {aVenir && !enCours && !commence(r) && (
        <button className="btn-ghost text-xs whitespace-nowrap" title="Changer la date, l'heure, la durée, le prof… (la famille et le prof sont prévenus)"
          onClick={() => onModifier(r)} data-testid="prives-modifier"><Pencil size={12} /> Modifier…</button>
      )}
      {aVenir && !enCours && (
        <button className="btn-ghost text-xs whitespace-nowrap" title="Annuler ce cours (la famille reçoit un e-mail)"
          onClick={() => onAnnuler(r)} data-testid="prives-annuler"><X size={12} /> Annuler…</button>
      )}
    </span>
  );
};

/* Annuler un cours, sous sa ligne : un mot pour la famille, le prof déplacé (payé), la
   facture de 50 % si la famille a téléphoné moins de 24 h avant (décochée d'office). Seuils
   et pourcentage : Réglages › Cours privés (`R`). */
const FormAnnulation = ({ r, R, valeur, onChange, onGarder, onConfirmer, occupe = false }) => (
  <div className="rounded-xl border p-3 space-y-2 text-xs" style={{ borderColor: "#FECACA", background: "#FEF2F2" }} data-testid="prives-annulation">
    <textarea className="input-field text-xs w-full bg-white" rows={2} placeholder="Un mot pour la famille (facultatif)" value={valeur.texte}
      onChange={(e) => onChange({ ...valeur, texte: e.target.value })} />
    <label className="flex items-start gap-1.5">
      <input type="checkbox" className="mt-0.5" checked={valeur.paye} onChange={(e) => onChange({ ...valeur, paye: e.target.checked })} data-testid="prives-prof-paye" />
      Le prof s'est déplacé (annulé {seuilDeplace(R)} avant) : il est payé{R.salaire ? ` (${pourRienTexte(R.salaire)})` : ""}
    </label>
    {tardPourEcole(r, R) && (
      <label className="flex items-start gap-1.5">
        <input type="checkbox" className="mt-0.5" checked={valeur.facturer} onChange={(e) => onChange({ ...valeur, facturer: e.target.checked })} data-testid="prives-facturer-tardive" />
        <span>La famille a annulé moins de {heures(R.ecole_facturer_heures)} avant : facturer {R.ecole_facturer_pourcentage} % ({chf((Number(r.montant || 0) * R.ecole_facturer_pourcentage) / 100)})</span>
      </label>
    )}
    <span className="flex gap-2 justify-end">
      <button className="btn-ghost text-xs bg-white" onClick={onGarder} disabled={occupe}>Garder</button>
      <button className="btn-primary text-xs" onClick={() => onConfirmer(r)} disabled={occupe} data-testid="prives-annuler-confirmer">
        {occupe ? "Un instant…" : "Annuler le cours"}</button>
    </span>
  </div>
);

/* Une ligne : l'heure, l'élève et la discipline, la personne à contacter, le prof, le prix,
   puis les gestes. La date n'est écrite qu'à la première ligne de son jour (comme l'agenda de
   l'iPhone) ; les lignes d'un même jour sont séparées d'un trait en retrait. */
const LigneCours = ({ r, R, premier, aVenir, couleur, annulation, setAnnulation, onAnnuler, onAbsent, onModifier, onVoirJour, occupe }) => {
  const annule = r.statut === "annulee";
  const eleve = `${r.eleve_prenom || r.prenom || ""} ${r.eleve_nom || ""}`.trim();
  const contact = `${r.prenom || ""} ${r.nom || ""}`.trim();
  const enCours = annulation.id === r.id;
  return (
    <div className="flex" style={premier ? { borderTop: "1px solid var(--border)" } : undefined} data-testid="prives-ligne">
      <div className="w-[72px] shrink-0 pl-3 py-2.5">{premier && <FeuilleDate date={r.date} onVoir={onVoirJour} />}</div>
      <div className="flex-1 min-w-0 pr-4 py-3" style={premier ? undefined : { borderTop: "1px solid #F1F5F9" }}>
        <div className="flex flex-wrap md:flex-nowrap items-start gap-x-4 gap-y-1.5">
          <div className={`w-full md:w-[92px] shrink-0 text-sm font-semibold tabular-nums ${annule ? "line-through text-[var(--muted)]" : ""}`}>
            {heure(r.debut)}–{heure(r.fin)}
            <span className="md:hidden font-normal text-[var(--muted)] no-underline"> · <span className="inline-block w-2 h-2 rounded-full align-middle" style={{ background: couleur }} /> {r.prof} · {chf(r.montant)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-sm truncate ${annule ? "text-[var(--muted)]" : ""}`}>
              <span className="font-semibold">{eleve}</span> <span className="text-[var(--muted)]">· {r.discipline_label} {r.minutes} min</span>
            </div>
            <div className="text-xs text-[var(--muted)] flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
              {contact && contact !== eleve && <span>{contact}</span>}
              {r.telephone && <a className="underline underline-offset-2 hover:text-[var(--text)] whitespace-nowrap" href={`tel:${r.telephone}`}>{r.telephone}</a>}
              {r.email && (
                <a className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-[var(--text)] min-w-0" href={`mailto:${r.email}`} title={`Écrire à ${r.email}`}>
                  <Mail size={11} className="shrink-0" /><span className="truncate">{r.email}</span>
                </a>
              )}
            </div>
            {r.objectif && <div className="text-xs text-[var(--muted)] italic truncate mt-0.5" title={r.objectif}>« {r.objectif} »</div>}
            <Changement r={r} />
          </div>
          <div className="hidden md:flex w-[104px] shrink-0 items-center gap-1.5 text-sm pt-px">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: couleur }} /><span className="truncate">{r.prof}</span>
          </div>
          <div className="hidden md:block w-[84px] shrink-0 text-sm text-right tabular-nums pt-px">{chf(r.montant)}</div>
          <div className="w-full md:w-[200px] shrink-0 flex md:justify-end">
            <EtatEtGestes r={r} aVenir={aVenir} enCours={enCours} onAbsent={onAbsent} onModifier={onModifier}
              onAnnuler={(x) => setAnnulation({ id: x.id, texte: "", paye: deplace(x, R), facturer: false })} />
          </div>
        </div>
        {enCours && (
          <div className="mt-3">
            <FormAnnulation r={r} R={R} valeur={annulation} onChange={setAnnulation} onConfirmer={onAnnuler} occupe={occupe}
              onGarder={() => setAnnulation({ id: "", texte: "", paye: false, facturer: false })} />
          </div>
        )}
      </div>
    </div>
  );
};
// « Voir les N autres », « Réduire » : un lien sur toute la largeur de la liste.
const LienListe = ({ children, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium hover:bg-[#F8FAFC] transition-colors"
    style={{ color: "#0E7490" }}>{children}</button>
);

export default function ReservationsPrivees({ cible }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  // « Ce mois » par défaut (Jen, 02.11.2026) ; un chiffre clé (ici ou au tableau de bord) en
  // demande une autre.
  const [vue, setVue] = useState(() => vueDe(cible) || "mois");
  // Repliée au-delà de LIMITE lignes, pour que le calendrier reste à portée de vue.
  const [deplie, setDeplie] = useState(false);
  const changerVue = (v) => { setVue(v); setDeplie(false); };
  // Un chiffre clé touché : la liste s'ouvre et vient à l'écran (Jen, 01.11.2026).
  const refListe = useRef(null);
  const [demande, setDemande] = useState(() => (vueDe(cible) ? 1 : 0));
  const servie = useRef(0);
  const voir = (v) => { changerVue(v); setDemande((n) => n + 1); };
  const [annulation, setAnnulation] = useState({ id: "", texte: "", paye: false, facturer: false });
  // « Absent·e — facturer » : la réservation dont la fenêtre est ouverte.
  const [absence, setAbsence] = useState(null);
  // « Modifier… » : la réservation dont la fenêtre est ouverte (02.11.2026).
  const [modif, setModif] = useState(null);
  const [ajout, setAjout] = useState(false);
  const refMessage = useRef(null);
  const [cal, setCal] = useState(null);
  const [mois, setMois] = useState(() => new Date().toISOString().slice(0, 7));
  // Toucher la date d'une ligne : ce jour dans le calendrier (le compteur rejoue la demande).
  const [voirJour, setVoirJour] = useState(null);
  const ouvrirJour = (date) => setVoirJour((v) => ({ date, n: (v?.n || 0) + 1 }));
  const chargerCal = useCallback(() => {
    api(`/reservations-privees/calendrier?du=${mois}-01&jours=42`).then(setCal).catch((e) => setErreur(e.message));
  }, [mois]);
  useEffect(() => { chargerCal(); }, [chargerCal]);
  useEffect(() => {
    if (demande <= servie.current || !d) return;
    servie.current = demande;
    setTimeout(() => refListe.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [demande, d]);
  const charger = useCallback(() => {
    api("/reservations-privees").then(setD).catch((e) => setErreur(e.message));
  }, []);
  useEffect(charger, [charger]);
  // Les disponibilités d'un prof : ce jour seulement, toutes les semaines (?toutes=true), ou
  // retour à la semaine type (plages = null). Une erreur reste dans la fenêtre.
  const enregistrerDispos = async (pid, jour, plages, toutes) => {
    setMessage("");
    if (plages === null) await api(`/reservations-privees/agenda/${pid}/jour/${jour}`, { method: "DELETE" });
    else await api(`/reservations-privees/agenda/${pid}/jour/${jour}${toutes ? "?toutes=true" : ""}`, { method: "PUT", body: JSON.stringify({ plages }) });
    const qui = d?.profs.find((p) => p.prof_id === pid)?.prenom || "";
    setMessage(toutes ? `Semaine type de ${qui} enregistrée.` : `Disponibilités de ${qui} enregistrées.`);
    charger(); chargerCal();
  };
  // Annulé par la famille moins d'1 h avant : l'école décide si elle crédite le prof.
  // Un geste à la fois (série de tests, 02.11.2026) : un double clic sur « Annuler le cours »
  // envoyait deux annulations — le serveur n'en garde qu'une, mais la seconde s'affichait en
  // rouge (« déjà annulée ») sous le message vert ; sur « Créditer le prof », deux e-mails au prof.
  const [occupe, setOccupe] = useState("");
  const enCours = useRef(false);
  const unSeul = async (cle, geste) => {
    if (enCours.current) return;
    enCours.current = true; setOccupe(cle);
    try { await geste(); } finally { enCours.current = false; setOccupe(""); }
  };
  const decider = (r, crediter) => unSeul(`paie-${r.id}`, async () => {
    setErreur(""); setMessage("");
    try {
      const x = await api(`/reservations-privees/${r.id}/paie-prof`, { method: "POST", body: JSON.stringify({ crediter }) });
      setD(x); setMessage(x.message);
    } catch (e) { setErreur(e.message); }
  });
  const annuler = (r) => unSeul(`annuler-${r.id}`, async () => {
    setErreur(""); setMessage("");
    try {
      const x = await api(`/reservations-privees/${r.id}/annuler`, { method: "POST", body: JSON.stringify({
        message: annulation.texte, prof_paye: annulation.paye, facturer: annulation.facturer && tardPourEcole(r, reglesDe(d?.regles)) }) });
      setMessage(x.message); setAnnulation({ id: "", texte: "", paye: false, facturer: false }); charger(); chargerCal();
    } catch (e) { setErreur(e.message); }
  });
  const absenceFaite = (x) => { setAbsence(null); setErreur(""); setD(x); setMessage(x.message); chargerCal(); };
  // Modifié : la liste et le calendrier à jour, le message (qui est prévenu) amené à l'écran.
  const modificationFaite = (x) => {
    setModif(null); setErreur(""); setD(x); setMessage(x.message); chargerCal();
    setTimeout(() => refMessage.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };
  // Ajouté : la liste et le calendrier à jour ; la fenêtre reste ouverte (« Encore un cours »).
  const ajoutFait = (x) => { setErreur(""); setD(x); setMessage(x.message); chargerCal(); };
  if (!d) return <p className="text-[var(--muted)]">{erreur || "Chargement…"}</p>;
  // Les règles de l'école (Réglages › Cours privés) : l'en-tête, « Annuler… », « Absent·e ».
  // Les règles des Réglages, et celle du salaire du prof (école seulement : « il est payé (50 % du
  // salaire + le déplacement) »).
  const R = { ...reglesDe(d.regles), salaire: d.salaires?.regle };
  const tard = partCourte(R.annulation_pourcentage);
  const absent = partCourte(R.absence_pourcentage);
  const toutes = [...d.a_venir, ...d.passees];
  const moisCourant = aujourdhui().slice(0, 7);
  const nomMois = NOMS_MOIS[Number(moisCourant.slice(5)) - 1];
  const parDate = (a, b) => `${a.date}${a.debut}`.localeCompare(`${b.date}${b.debut}`);
  const listes = {
    // Le mois : ses cours réservés, passés et à venir (comme le chiffre « En novembre »).
    mois: toutes.filter((r) => r.statut !== "annulee" && String(r.date).startsWith(moisCourant)).sort(parDate),
    a_venir: d.a_venir,
    passees: d.passees,
    // Les absents sans annulation (facturés) se rangent avec les annulés, comme au chiffre clé.
    annulees: toutes.filter((r) => (r.statut === "annulee" || r.absent) && r.date >= debutSaison()).sort((a, b) => parDate(b, a)),
  };
  const lignes = listes[vue] || listes.mois;
  const aVenir = new Set(d.a_venir.map((r) => r.id));
  const couleurs = Object.fromEntries((d.profs || []).map((p) => [p.prof_id, p.couleur]));
  const nbAVenir = d.a_venir.filter((r) => r.statut !== "annulee").length;
  // Repliée : LIMITE lignes. Pour « ce mois », la fenêtre commence au prochain cours (ceux déjà
  // passés ce mois-ci restent à un toucher, au-dessus) ; ailleurs, au début de la liste.
  let depart = 0;
  if (vue === "mois") {
    const i = lignes.findIndex((r) => !commence(r));
    depart = Math.max(0, Math.min(i < 0 ? lignes.length : i, lignes.length - LIMITE));
  }
  const visibles = deplie ? lignes : lignes.slice(depart, depart + LIMITE);
  const avant = deplie ? 0 : depart;
  const apres = deplie ? 0 : Math.max(0, lignes.length - depart - LIMITE);
  const vide = { mois: `Aucun cours privé en ${nomMois}.`, a_venir: "Aucun cours privé réservé.",
    annulees: "Aucun cours annulé cette saison." }[vue] || "Aucun cours passé.";
  return (
    <div className="space-y-6" data-testid="console-reservations-privees">
      {/* Les règles en quatre cases courtes (Jen, 03.11.2026 : « garder que ce qui compte —
          là, ça ne donne pas envie de lire ») ; le détail est dans Réglages › Cours privés. */}
      <EnTeteMenu ton="application" Icon={CalendarClock} contexte="Réservés sur le site ou par l'école" titre="Cours privés" testid="prives-entete"
        droite={(
          <span className="inline-flex flex-wrap gap-2">
            <button type="button" className="btn-primary text-xs" onClick={() => setAjout(true)} data-testid="prives-ajouter"><Plus size={12} /> Ajouter un cours</button>
            <a href={d.lien_site} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs"><ExternalLink size={12} /> Page de réservation</a>
            <button type="button" className="btn-ghost text-xs" onClick={versReglages} data-testid="prives-vers-reglages"><Settings2 size={12} /> Réglages des cours privés</button>
          </span>
        )}
        points={[
            [CalendarDays, "Réservation", `De ${heures(R.delai_heures)} à ${R.horizon_texte} à l'avance`],
            [Wallet, "Paiement", "Sur place, cash ou TWINT"],
            [Undo2, "Annulation", tard.texte === absent.texte
              ? `Gratuite jusqu'à ${heures(R.annulation_heures)} avant ; plus tard ou absent·e : ${tard.texte}`
              : `Gratuite jusqu'à ${heures(R.annulation_heures)} avant, puis ${tard.texte} ; absent·e : ${absent.texte}`],
            [HandCoins, "Prof", "Salaire en cash, hors décomptes"],
          ]} />
      {/* Les chiffres clés, comme sur les autres menus (Jen, 31.10.2026). */}
      {d.chiffres && <ChiffresPrives d={d.chiffres} onVoir={voir} />}
      {message && <div ref={refMessage} className="rounded-2xl px-4 py-3 text-sm scroll-mt-6" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }} data-testid="prives-message">{message}</div>}
      {erreur && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}>{erreur}</div>}

      {(d.a_confirmer || []).length > 0 && (
        <div className="rounded-2xl border p-4 space-y-3" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }} data-testid="prives-a-confirmer">
          <div className="font-semibold text-sm" style={{ color: "#B45309" }}>À confirmer · {d.a_confirmer.length} cours annulé{d.a_confirmer.length > 1 ? "s" : ""} {seuilDeplace(R)} avant</div>
          <p className="text-xs" style={{ color: "#92400E" }}>
            La famille a annulé alors que le prof était peut-être déjà en route. S'il s'est déplacé, « Créditer le prof » : il touche {pourRienTexte(d.salaires?.regle)}, en cash (Noémie et Miller : rien).
            Rien ne change pour la famille ({tard.texte === "la moitié" ? "la moitié du prix" : tard.texte} reste {dueAccord(tard.f)}, comme toute annulation à moins de {heures(R.annulation_heures)}).
          </p>
          {d.a_confirmer.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border p-3 text-sm flex flex-wrap items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid="prives-a-confirmer-ligne">
              <div className="flex-1 min-w-[220px]">
                <strong>{r.prof}</strong> · {r.quand} ({r.debut}–{r.fin})
                <div className="text-xs text-[var(--muted)]">{r.discipline_label} · {r.minutes} min · {r.eleve_prenom} {r.eleve_nom}{r.annulee_le ? ` · annulé le ${new Date(r.annulee_le).toLocaleString("fr-CH", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}</div>
              </div>
              <button className="btn-primary text-xs" onClick={() => decider(r, true)} disabled={!!occupe} data-testid="prives-crediter">Créditer le prof</button>
              <button className="btn-ghost text-xs" onClick={() => decider(r, false)} disabled={!!occupe} data-testid="prives-pas-crediter">Ne pas créditer</button>
            </div>
          ))}
        </div>
      )}

      {/* 1. Les cours réservés, en premier (Jen, 02.11.2026). */}
      <div ref={refListe} className="scroll-mt-6">
      <Section titre="Cours réservés" testid="prives-reservations"
        aide="Toucher une date : ce jour s'ouvre dans le calendrier, plus bas."
        action={<Puces valeur={vue} onChange={changerVue} testid="prives-vue" options={[
          { valeur: "mois", label: `En ${nomMois}`, nb: listes.mois.length, testid: "prives-vue-mois" },
          // Le nombre : les cours réservés (comme « À venir » en tête) ; les annulés restent
          // dans la liste, avec leur badge.
          { valeur: "a_venir", label: "À venir", nb: nbAVenir, testid: "prives-vue-a_venir" },
          { valeur: "passees", label: "Passés", testid: "prives-vue-passees" },
          { valeur: "annulees", label: "Annulés", nb: listes.annulees.length, testid: "prives-vue-annulees" }]} />}>
        <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="prives-liste">
          {!lignes.length ? (
            <div className="px-5 py-6 text-sm text-[var(--muted)]" data-testid="prives-liste-vide">
              {vide}
              {vue === "mois" && nbAVenir > 0 && (
                <button type="button" className="ml-2 font-medium underline underline-offset-2" style={{ color: "#0E7490" }} onClick={() => changerVue("a_venir")}>
                  Voir les {nbAVenir} cours à venir
                </button>
              )}
            </div>
          ) : (
            <>
              {avant > 0 && (
                <LienListe onClick={() => setDeplie(true)} testid="prives-liste-avant">
                  <ChevronUp size={15} /> {avant === 1 ? `1 cours plus tôt en ${nomMois}` : `${avant} cours plus tôt en ${nomMois}`}
                </LienListe>
              )}
              <div className={avant > 0 ? "" : "-mt-px"}>
                {visibles.map((r, k) => (
                  <LigneCours key={r.id} r={r} R={R} premier={!k || visibles[k - 1].date !== r.date} aVenir={aVenir.has(r.id)}
                    couleur={couleurs[r.prof_id] || "#94A3B8"} annulation={annulation} setAnnulation={setAnnulation}
                    onAnnuler={annuler} onAbsent={setAbsence} onModifier={setModif} onVoirJour={ouvrirJour} occupe={occupe === `annuler-${r.id}`} />
                ))}
              </div>
              {apres > 0 && (
                <div className="border-t" style={{ borderColor: "var(--border)" }}>
                  <LienListe onClick={() => setDeplie(true)} testid="prives-liste-plus">
                    Voir {apres === 1 ? "l'autre cours" : `les ${apres} autres`} <ChevronDown size={15} />
                  </LienListe>
                </div>
              )}
              {deplie && lignes.length > LIMITE && (
                <div className="border-t" style={{ borderColor: "var(--border)" }}>
                  <LienListe onClick={() => setDeplie(false)} testid="prives-liste-moins">Réduire <ChevronUp size={15} /></LienListe>
                </div>
              )}
            </>
          )}
        </div>
      </Section>
      </div>

      {/* 2. Le calendrier : les jours réservés, la journée de chaque prof, les disponibilités. */}
      <Section titre="Calendrier" testid="prives-calendrier"
        aide="En vert, les jours qui ont un cours réservé. Toucher un jour : la journée de chaque prof — ses disponibilités (teinte du prof), ses cours privés réservés (vert) et ses cours de l'école (gris). « Modifier les disponibilités », le crayon d'une colonne ou un clic sur une dispo : ce jour seulement ou toutes les semaines. En bas, la semaine type de chacun.">
        {!cal ? <p className="text-sm text-[var(--muted)]">Chargement…</p>
          : <CalendrierPrives cal={cal} mois={mois} onMois={setMois} editables={d.profs.map((p) => p.prof_id)} onEnregistrer={enregistrerDispos}
              onAjouter={(x) => setAjout(x)}
              voirJour={voirJour} aujourdhui={aujourdhui()} />}
      </Section>

      {/* 3. Le salaire que l'école remet aux profs (02.11.2026). */}
      <SalairesDesProfs salaires={d.salaires} seuil={seuilDeplace(R)} />

      {absence && <ModaleAbsence r={absence} R={R} regle={d.salaires?.regle} onFermer={() => setAbsence(null)} onFait={absenceFaite} />}
      {modif && <ModaleModification r={modif} onFermer={() => setModif(null)} onFait={modificationFaite} />}
      {ajout && <ModaleAjout depart={ajout === true ? null : ajout} onFermer={() => setAjout(false)} onFait={ajoutFait} />}
    </div>
  );
}
